import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { TrendUp, TrendDown, WarningCircle, ArrowRight } from "@phosphor-icons/react";
import client from "../lib/api";

/**
 * Bracket Drift Alerts — pulses on the Dashboard when a broker's price
 * bracket (median win + P25 rejection) has moved >10% between the last
 * 30 days and the prior 60d window.
 *
 * Direction "up"   → broker paying more (emerald tint) → push your asks higher
 * Direction "down" → broker tightening   (rose tint)   → your saved pricing
 *                                                       is losing you bids
 *
 * Auto-hides when no alerts are firing.
 */
export default function BracketDriftCard() {
  const [alerts, setAlerts] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    client.get("/loadboard/analytics/bracket-drift?limit=3&min_shift_pct=10")
      .then((r) => alive && setAlerts(r.data.alerts || []))
      .catch((e) => alive && toast.error(e?.response?.data?.detail || "Drift alerts failed"))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <div className="card-surface p-6" data-testid="drift-card-loading">
        <div className="animate-pulse text-neutral-500 text-sm">Checking broker bracket drift…</div>
      </div>
    );
  }
  if (!alerts || alerts.length === 0) return null;

  const upCount = alerts.filter(a => a.direction === "up").length;
  const downCount = alerts.filter(a => a.direction === "down").length;
  const headline = downCount > 0
    ? `${downCount} broker${downCount === 1 ? "" : "s"} tightening — your saved pricing may be stale`
    : `${upCount} broker${upCount === 1 ? "" : "s"} paying more than they used to`;

  return (
    <div className="card-surface p-6" data-testid="drift-card">
      <div className="flex items-start justify-between gap-3 mb-5 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-amber-300 mb-1">
            <WarningCircle size={13} weight="bold" />
            BRACKET DRIFT ALERT
          </div>
          <h3 className="font-display text-lg font-semibold text-white">{headline}</h3>
          <p className="text-[11px] text-neutral-500 mt-1 max-w-md">
            Broker rate brackets that shifted &gt;10% between the last 30d and the prior 60d.{" "}
            Reprice your saved lanes to keep up with what the market is actually paying.
          </p>
        </div>
      </div>

      <div className="space-y-3" data-testid="drift-rows">
        {alerts.map((a) => (
          <DriftRow key={a.broker_company} alert={a} />
        ))}
      </div>

      <div className="mt-4 flex justify-end">
        <Link
          to="/app/loadboard/analytics"
          data-testid="drift-view-analytics"
          className="inline-flex items-center gap-1.5 text-xs text-neutral-400 hover:text-[#FF5A1F] transition-colors"
        >
          See broker brackets <ArrowRight size={12} weight="bold" />
        </Link>
      </div>
    </div>
  );
}


function DriftRow({ alert: a }) {
  const isUp = a.direction === "up";
  const tone = isUp
    ? "border-emerald-500/30 bg-emerald-500/[0.06]"
    : "border-rose-500/30 bg-rose-500/[0.06]";
  const IconEl = isUp ? TrendUp : TrendDown;
  const iconTone = isUp ? "text-emerald-300" : "text-rose-300";
  const headlineTone = isUp ? "text-emerald-200" : "text-rose-200";
  const headlineText = isUp
    ? "Paying more than before"
    : "Tightening — closing lower";
  const shiftPill = isUp
    ? "border-emerald-500/40 bg-emerald-500/15 text-emerald-300"
    : "border-rose-500/40 bg-rose-500/15 text-rose-300";
  const shiftLabel = isUp ? `+${a.shift_pct}%` : `${a.shift_pct}%`;

  // Action copy — the specific move the pilot should make
  const action = isUp
    ? "You can push your anchor higher — market moved in your favor."
    : "Recalibrate your saved pricing — asking the same rate as last quarter will lose bids.";

  const brokerSlug = (a.broker_company || "").replace(/\s+/g, '-').toLowerCase();

  return (
    <div className={`rounded-lg border p-4 ${tone}`} data-testid={`drift-row-${brokerSlug}`}>
      <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <IconEl size={13} weight="bold" className={iconTone} />
            <div className="text-sm font-semibold text-white truncate">{a.broker_company}</div>
          </div>
          <div className={`text-[11px] mt-0.5 uppercase tracking-wider font-medium ${headlineTone}`}>
            {headlineText}
          </div>
        </div>
        <div className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold tabular-nums ${shiftPill}`}>
          {shiftLabel}
        </div>
      </div>

      {/* Was X, Now Y comparison — the money math */}
      <div className="grid grid-cols-2 gap-3 mt-3 text-xs">
        <div className="rounded-md bg-white/[0.03] border border-white/5 px-3 py-2">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-0.5">Prior 60d</div>
          <div className="tabular-nums text-neutral-300">
            {a.prior_floor_per_mile != null && (
              <>Floor <b>${a.prior_floor_per_mile.toFixed(2)}</b></>
            )}
            {a.prior_floor_per_mile != null && a.prior_ceiling_per_mile != null && " · "}
            {a.prior_ceiling_per_mile != null && (
              <>Ceiling <b>${a.prior_ceiling_per_mile.toFixed(2)}</b></>
            )}
          </div>
        </div>
        <div className={`rounded-md border px-3 py-2 ${isUp ? "border-emerald-500/25 bg-emerald-500/[0.04]" : "border-rose-500/25 bg-rose-500/[0.04]"}`}>
          <div className={`text-[10px] uppercase tracking-widest mb-0.5 ${isUp ? "text-emerald-400" : "text-rose-400"}`}>Last 30d</div>
          <div className="tabular-nums text-white">
            {a.current_floor_per_mile != null && (
              <>Floor <b>${a.current_floor_per_mile.toFixed(2)}</b></>
            )}
            {a.current_floor_per_mile != null && a.current_ceiling_per_mile != null && " · "}
            {a.current_ceiling_per_mile != null && (
              <>Ceiling <b>${a.current_ceiling_per_mile.toFixed(2)}</b></>
            )}
          </div>
        </div>
      </div>

      <div className="text-[11px] text-neutral-400 mt-2 leading-relaxed">{action}</div>
    </div>
  );
}
